import React from 'react';

import SquarePenIcon from 'assets/icons/square-pen.inline.svg';
import PanelLeftIcon from 'assets/icons/panel-left.inline.svg';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import SidePeekIcon from 'assets/icons/side-peek.inline.svg';
import Tooltip from '@mui/material/Tooltip';
import { Button, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';

import useChatAgentDialog from 'components/Dialogs/ChatAgentDialog/dispatcher';
import { trackingEvents } from 'containers/SimplifiAiAssistant/AiAssistant/aiEvents';
import { tracker } from 'app-components/utils/core';
import { isDevMode } from 'app-components/utils/core/config';
import { useChatSession } from 'components/ChatLauncher/chatSession';
type ChatHeaderProps = {
  onClose: () => void;
  handleMinimize: () => void;
  handleDock: () => void;
  isDocked: boolean;
  setShowSideBarMenu: React.Dispatch<React.SetStateAction<boolean>>;
  handleRef: React.RefObject<HTMLDivElement>;
  isDefaultState?: boolean;
};

const ChatHeader = ({
  onClose,
  handleMinimize,
  handleDock,
  isDocked,
  setShowSideBarMenu,
  handleRef,
  isDefaultState,
}: ChatHeaderProps): JSX.Element => {
  const openChatAgentDialog = useChatAgentDialog();
  const { startNewConversation } = useChatSession();

  // Detect platform for keyboard shortcut display
  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const cmdKey = isMac ? '⌘' : 'Ctrl';

  const handleNewConversation = async () => {
    tracker.track(trackingEvents.AIChatNew);
    tracker.track(trackingEvents.AIChatSaved);
    if (isDevMode()) {
      openChatAgentDialog();
    } else {
      await startNewConversation();
    }
  };

  const handleOpenSidebar = () => {
    setShowSideBarMenu((prev) => !prev);
  };

  const handleClose = () => {
    tracker.track(trackingEvents.AIChatClosed);
    tracker.track(trackingEvents.AIChatSaved);
    onClose();
  };

  const theme = useTheme();
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        width: '100%',
        zIndex: 10,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(to bottom, ${theme.palette.background.paper} 80%, transparent)`,
          zIndex: 0,
          userSelect: 'none',
        }}
      />
      <Stack
        direction='row'
        alignItems='center'
        padding={2}
        ref={handleRef}
        sx={{
          position: 'relative',
          zIndex: 10,
          cursor: isDocked ? 'default' : 'grab',
          gap: 1,
        }}
      >
        <Stack gap={1} direction='row' sx={{ flexShrink: 0 }}>
          <Tooltip title='Open Sidebar'>
            <IconButton variant='tonal' size='small' onClick={handleOpenSidebar} sx={{ cursor: 'w-resize' }}>
              <PanelLeftIcon width={18} height={18} />
            </IconButton>
          </Tooltip>
          {!isDefaultState && (
            <Tooltip title='New Chat'>
              <Button
                variant='outlined'
                size='small'
                onClick={handleNewConversation}
                color='primary'
                startIcon={<SquarePenIcon width={18} height={18} />}
              >
                New
              </Button>
            </Tooltip>
          )}
        </Stack>

        <Box sx={{ minWidth: 0, flexGrow: 1, overflow: 'hidden' }} />

        <Stack gap={0.5} direction='row' alignItems='center' justifyContent='flex-end' sx={{ flexShrink: 0 }}>
          <Tooltip title={isDocked ? `Close split view (${cmdKey}.)` : `Open split view (${cmdKey}.)`}>
            <IconButton variant='tonal' size='small' onClick={handleDock} color={isDocked ? 'primary' : 'default'}>
              <SidePeekIcon width={20} height={20} />
            </IconButton>
          </Tooltip>
          {!isDocked && (
            <Tooltip title='Minimize'>
              <IconButton variant='tonal' size='small' onClick={handleMinimize}>
                <RemoveRoundedIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title='Close'>
            <IconButton variant='tonal' size='small' onClick={handleClose}>
              <CloseRoundedIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ChatHeader;
